/**
 * Trip Import Modal Component
 *
 * Provides drag-and-drop interface for importing trip data from ZIP or JSON files
 * Includes import preview and validation. Import logic automatically handled by backend:
 * - If trip ID doesn't exist in database → imports as new trip with new IDs
 * - If trip ID exists → prompts for confirmation → deletes existing → imports with new IDs
 */

import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper
} from '@mui/material';
import {
  Upload as UploadIcon,
  Archive as ZipIcon,
  DataObject as JsonIcon,
  CloudUpload as CloudUploadIcon,
  Description as FileIcon,
  Visibility as PreviewIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  DragIndicator as DragIcon
} from '@mui/icons-material';

import tripImportService from '../../services/tripImportService';

const TripImportModal = ({ open, onClose, context, controller, onImportSuccess }) => {
  const [step, setStep] = useState('upload'); // upload, preview, importing, complete
  const [selectedFile, setSelectedFile] = useState(null);
  const [importData, setImportData] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [error, setError] = useState(null);
  const [validationResults, setValidationResults] = useState(null);
  const [existingTrip, setExistingTrip] = useState(null);

  const supportedFormats = [
    { ext: 'zip', label: 'ZIP Archive', description: 'Complete trip backup with documents', icon: ZipIcon },
    { ext: 'json', label: 'JSON File', description: 'Trip data only (no documents)', icon: JsonIcon }
  ];

  const resetState = () => {
    setStep('upload');
    setSelectedFile(null);
    setImportData(null);
    setIsProcessing(false);
    setImportProgress(0);
    setError(null);
    setValidationResults(null);
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelection(files[0]);
    }
  }, []);

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelection(file);
    }
  };

  const handleFileSelection = async (file) => {
    setError(null);
    setSelectedFile(file);
    setExistingTrip(null);

    // Validate file
    const validation = await validateFile(file);
    if (!validation.valid) {
      setError(validation.error);
      return;
    }

    // Process file for preview
    setIsProcessing(true);
    try {
      const preview = await tripImportService.previewImport(file);
      setImportData(preview);
      setValidationResults(preview.validation);

      // Check if trip already exists for this user
      if (context?.user && preview.tripData?.trip?.id) {
        const tripExists = await controller.checkExistingTrip(
          context.user.uid,
          preview.tripData.trip.id
        );
        setExistingTrip(tripExists);
      }

      setStep('preview');
    } catch (error) {
      setError('Failed to process import file: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const validateFile = async (file) => {
    const maxSize = 50 * 1024 * 1024; // 50MB
    const allowedExtensions = ['zip', 'json'];

    if (file.size > maxSize) {
      return { valid: false, error: 'File too large. Maximum size is 50MB.' };
    }

    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!allowedExtensions.includes(extension)) {
      return { valid: false, error: 'Unsupported file format. Only ZIP and JSON files are supported.' };
    }

    return { valid: true };
  };

  const handleImport = async () => {
    if (!selectedFile || !importData) return;

    setIsProcessing(true);
    setStep('importing');
    setError(null);

    // Simulate progress
    const progressInterval = setInterval(() => {
      setImportProgress(prev => {
        if (prev >= 90) return prev;
        return prev + Math.random() * 10;
      });
    }, 500);

    try {
      // Pass controller in context to match TripExportModal pattern
      const contextWithController = { ...context, controller };

      const result = await tripImportService.importTrip(
        selectedFile,
        importData,
        contextWithController
      );

      clearInterval(progressInterval);
      setImportProgress(100);

      if (result.success) {
        setStep('complete');
        setTimeout(() => {
          onClose();
          resetState();
          // Navigate to Trips page to show newly imported trip
          if (onImportSuccess) {
            onImportSuccess();
          }
        }, 2000);
      } else {
        setError(result.error || 'Import failed');
        setStep('preview');
      }
    } catch (error) {
      clearInterval(progressInterval);
      setError(error.message || 'Import failed');
      setStep('preview');
    } finally {
      setTimeout(() => setIsProcessing(false), 1000);
    }
  };

  const handleClose = () => {
    if (!isProcessing || step === 'complete') {
      resetState();
      onClose();
    }
  };

  const handleBack = () => {
    if (step === 'preview') {
      setStep('upload');
      setSelectedFile(null);
      setImportData(null);
      setError(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={isProcessing}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <UploadIcon color="primary" />
          Import Trip Data
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Step 1: File Upload */}
        {step === 'upload' && (
          <>
            <Typography variant="body1" gutterBottom>
              Import trip data from a ZIP backup or JSON export file
            </Typography>

            {/* Drag & Drop Area */}
            <Paper
              sx={{
                border: isDragOver ? 2 : 1,
                borderColor: isDragOver ? 'primary.main' : 'grey.300',
                borderStyle: 'dashed',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                bgcolor: isDragOver ? 'primary.50' : 'grey.50',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                mb: 3
              }}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById('file-input').click()}
            >
              <input
                id="file-input"
                type="file"
                accept=".zip,.json"
                style={{ display: 'none' }}
                onChange={handleFileInputChange}
              />

              <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                {isProcessing ? (
                  <CircularProgress />
                ) : (
                  <>
                    <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                    <Typography variant="h6">
                      {isDragOver ? 'Drop your file here' : 'Drag & drop your file here'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      or click to browse files
                    </Typography>
                  </>
                )}
              </Box>
            </Paper>

            {/* Supported Formats */}
            <Typography variant="subtitle2" gutterBottom>
              Supported Formats
            </Typography>
            <List dense>
              {supportedFormats.map((format) => (
                <ListItem key={format.ext}>
                  <ListItemIcon>
                    <format.icon />
                  </ListItemIcon>
                  <ListItemText
                    primary={format.label}
                    secondary={format.description}
                  />
                  <Chip label={`.${format.ext}`} size="small" variant="outlined" />
                </ListItem>
              ))}
            </List>
          </>
        )}

        {/* Step 2: Preview & Configuration */}
        {step === 'preview' && importData && (
          <>
            <Box mb={2}>
              <Typography variant="h6" gutterBottom>
                Import Preview
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <FileIcon />
                <Typography variant="body1">{selectedFile.name}</Typography>
                <Chip label={formatFileSize(selectedFile.size)} size="small" variant="outlined" />
              </Box>
            </Box>

            {/* Validation Results */}
            {validationResults && (
              <Alert
                severity={validationResults.valid ? 'success' : 'error'}
                sx={{ mb: 2 }}
                icon={validationResults.valid ? <CheckIcon /> : <ErrorIcon />}
              >
                {validationResults.valid
                  ? 'Import file validated successfully'
                  : `Validation failed: ${validationResults.error}`
                }
              </Alert>
            )}

            {/* Existing Trip Warning */}
            {existingTrip && (
              <Alert
                severity="warning"
                sx={{ mb: 2 }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  <strong>Trip Already Exists</strong>
                </Typography>
                <Typography variant="body2">
                  A trip with this name already exists in your account. Importing will:
                </Typography>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  <li><strong>Delete the existing trip completely</strong> (including all days, accommodations, places, travel, and documents)</li>
                  <li>Replace it with the imported data</li>
                  <li>Generate new IDs for all entities (item-to-item links, e.g. hotel-to-place, are remapped to the new IDs automatically)</li>
                </ul>
                <Typography variant="body2" color="error.main">
                  <strong>This action cannot be undone. Make sure you have a backup of the existing trip if needed.</strong>
                </Typography>
              </Alert>
            )}

            {/* Trip Preview */}
            {importData.tripData && (
              <Accordion defaultExpanded sx={{ mb: 3 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">
                    {importData.tripData.trip?.name || 'Imported Trip'} - Content Preview
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                    <Chip label={`${importData.contentSummary?.days || 0} days`} size="small" />
                    <Chip label={`${importData.contentSummary?.places || 0} places`} size="small" />
                    <Chip label={`${importData.contentSummary?.accommodation || 0} accommodations`} size="small" />
                    <Chip label={`${importData.contentSummary?.travel || 0} travel`} size="small" />
                    <Chip label={`${importData.contentSummary?.documents || 0} documents`} size="small" />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Destination:</strong> {importData.tripData.trip?.destination || 'N/A'}<br />
                    <strong>Dates:</strong> {importData.tripData.trip?.startDate} to {importData.tripData.trip?.endDate}<br />
                    <strong>Export Date:</strong> {importData.manifest?.exportMetadata?.exportDate
                      ? new Date(importData.manifest.exportMetadata.exportDate).toLocaleDateString()
                      : 'N/A'
                    }
                  </Typography>
                </AccordionDetails>
              </Accordion>
            )}
          </>
        )}

        {/* Step 3: Import Progress */}
        {step === 'importing' && (
          <Box textAlign="center">
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Importing Trip Data...
            </Typography>
            <LinearProgress
              variant="determinate"
              value={importProgress}
              sx={{ mb: 2, height: 8, borderRadius: 4 }}
            />
            <Typography variant="body2" color="text.secondary">
              {Math.round(importProgress)}% complete - Please don't close this window
            </Typography>
          </Box>
        )}

        {/* Step 4: Complete */}
        {step === 'complete' && (
          <Box textAlign="center">
            <CheckIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Import Completed Successfully!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your trip data has been imported. The page will refresh automatically.
            </Typography>
          </Box>
        )}

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        {step === 'preview' && (
          <Button onClick={handleBack} color="inherit">
            Back
          </Button>
        )}

        <Button
          onClick={handleClose}
          disabled={isProcessing && step !== 'complete'}
          color="inherit"
        >
          {step === 'complete' ? 'Close' : 'Cancel'}
        </Button>

        {step === 'preview' && validationResults?.valid && (
          <Button
            onClick={handleImport}
            variant="contained"
            disabled={isProcessing}
            startIcon={<UploadIcon />}
            color={existingTrip ? 'warning' : 'primary'}
          >
            {existingTrip ? 'Replace Existing Trip' : 'Import Trip'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TripImportModal;